import { MarketingConsent } from '@iheartjane/menu';
import {
  Box,
  Button,
  Flex,
  Grid,
  Link,
  Typography,
  useDesktopMediaQuery,
} from '@iheartjane/reefer';
import { Form, useFormContext } from '@iheartjane/reefer-hook-form';
import lowerCase from 'lodash-es/lowerCase';
import { useEffect } from 'react';

import { useCheckout } from '@jane/shared-ecomm/providers';

import { CheckoutAccordionItem } from '../CheckoutAccordion/CheckoutAccordionItem';
import { StyledGridContainer } from '../checkout.styles';
import { useFulfillmentSchedule } from '../util/hooks/useFulfillmentSchedule';
import { useReservationModeLabels } from '../util/hooks/useReservationModeLabels';
import { AgreementLabel } from './AgreementLabel';
import { AgreementLinks } from './AgreementLinks';
import { DeliverySummary } from './DeliverySummary';
import FormattedAddress from './FormattedAddress';
import { useCheckoutOptionsForm } from './useCheckoutOptionsForm';

export const CheckoutDeliveryOptions = () => {
  const isDesktop = useDesktopMediaQuery({ size: 'sm', width: 'min' });

  const { consentItems, consentsValid, handleContinue, isValid } =
    useCheckoutOptionsForm();

  const { trigger } = useFormContext();

  const {
    appInfo: { appMode, bloomUserExperience },
    cart: { reservation_mode },
    deliveryAddress,
    deliveryValidation: { data: deliveryValidationData },
    dispatches: { openCart },
    store: { delivery_message_enabled },
  } = useCheckout();

  const shouldValidate = reservation_mode === 'delivery';

  const { deliverySlots, isLoadingDelivery } = useFulfillmentSchedule();
  const { deliveryLabel } = useReservationModeLabels();

  const slotOptions =
    deliverySlots?.map((slot) => ({
      label: slot.display_name,
      value: `${slot.from}>${slot.to}`,
    })) || [];

  useEffect(() => {
    // When switching to delivery, we re-validate pickup fields
    // as they are no longer required
    trigger('pickupOptions');
  }, []);

  return (
    <CheckoutAccordionItem
      id="options"
      header="Confirm your reservation details"
      summary={<DeliverySummary />}
      mt={bloomUserExperience && !isDesktop ? 16 : 0}
    >
      <Box>
        {/* Set to height: 'auto' */}
        <StyledGridContainer spacing={24}>
          {deliveryAddress?.street ? (
            <Grid.Item xs={12}>
              <Typography mb={12} variant="body-bold">
                Address
              </Typography>
              <Flex justifyContent="space-between" alignItems="center">
                <Box>
                  <FormattedAddress address={deliveryAddress} />
                </Box>
                <Link onClick={() => openCart()}>Change</Link>
              </Flex>

              {deliveryValidationData.can_deliver === false && (
                <Typography mt={12} color="error">
                  {deliveryLabel} is not available for this address.
                </Typography>
              )}
            </Grid.Item>
          ) : (
            <Grid.Item xs={12}>
              <Link onClick={() => openCart()}>
                Add {lowerCase(deliveryLabel)} address
              </Link>
            </Grid.Item>
          )}

          <Grid.Item xs={12} lg={6}>
            <Form.SelectField
              borderRadius="xs"
              disableMobileInputStyling
              label={`${deliveryLabel} time`}
              name="deliveryOptions.fulfillmentTime"
              loading={isLoadingDelivery}
              options={slotOptions}
              placeholder={
                !deliveryAddress?.street
                  ? `Add ${lowerCase(deliveryLabel)} address`
                  : slotOptions.length
                    ? 'Select a reservation slot'
                    : 'No reservation times available'
              }
              validate={(value) =>
                !shouldValidate ||
                !!value ||
                `Please select a ${lowerCase(deliveryLabel)} time.`
              }
              shouldUnregister={false}
            />
          </Grid.Item>
        </StyledGridContainer>

        {delivery_message_enabled && (
          <Form.TextField
            mt={24}
            disableMobileInputStyling
            borderRadius="xs"
            label={`${deliveryLabel} instructions`}
            name="deliveryOptions.message"
            placeholder="e.g. Gate code"
            maxLength={300}
            shouldUnregister={false}
          />
        )}

        {!!consentItems.length && (
          <Box mt={24}>
            {consentItems.map((item) => (
              <Box key={item.id}>
                <Form.CheckboxField
                  mt={12}
                  label={
                    <AgreementLabel
                      required={item.required}
                      label={item.label}
                    />
                  }
                  name={`deliveryOptions.${item.name}`}
                  aria-describedby={
                    item.external_link || item.external_links.length > 0
                      ? `agreement-links-${item.id}`
                      : undefined
                  }
                  validate={(value) =>
                    !shouldValidate ||
                    !!value ||
                    !item.required ||
                    'This is a required agreement.'
                  }
                  shouldUnregister={false}
                />
                {(item.external_link || item.external_links.length > 0) && (
                  <AgreementLinks
                    id={`agreement-links-${item.id}`}
                    agreementLabel={item.label}
                    external_link={item.external_link}
                    external_links={item.external_links}
                  />
                )}
              </Box>
            ))}

            {!consentsValid && (
              <Typography color="error" mt={12}>
                Please agree to the required terms.
              </Typography>
            )}

            {appMode === 'default' && (
              <Flex mt={12} flexDirection="row">
                <Form.CheckboxField
                  disableMobileInputStyling
                  name="deliveryOptions.promotionsOptIn"
                  label="Promotional emails"
                  labelHidden
                  defaultChecked
                />
                <MarketingConsent />
              </Flex>
            )}
          </Box>
        )}

        <Button
          mt={24}
          full
          label="Continue"
          onClick={handleContinue}
          disabled={!deliveryValidationData.can_deliver || !isValid}
        />
      </Box>
    </CheckoutAccordionItem>
  );
};
